<template>
  <div>
    <ChometzEmailModal
      v-if="showEmailModal && selectedCompany"
      :company="selectedCompany"
      :chometzSaleSeason="chometzSaleSeason"
      @closeModal="showEmailModal = false"
    />
    <ChometzAttachmentsModal
      v-if="showAttachmentsModal && selectedCompany"
      :company="selectedCompany"
      :chometzSaleSeason="chometzSaleSeason"
      @closeModal="showAttachmentsModal = false"
    />
    <Grid
      class="chometz-companies-grid"
      :pageable="pageable"
      :data-items="companies"
      :skip="skip"
      :take="take"
      :total="totalCompanies"
      :columns="columns"
      :loader="companiesLoading"
      :filter="filter"
      :column-menu="!companiesLoading"
      :resizable="true"
      ref="gridRef"
      @columnresize="onColumnResize"
      @filterchange="filterChangeHandler"
      @sortchange="sortChangeHandler"
      @pagechange="pageChangeHandler"
      data-cy="chometz-companies-grid"
    >
      <template v-slot:DetailsCell="{ props: { dataItem } }">
        <td class="k-table-td">
          <input
            class="fa fa-external-link details-icon"
            type="image"
            alt="Detail Icon"
            @click.prevent="openDetails(dataItem)"
            :src="detailIconUrl"
          />
        </td>
      </template>
      <template v-slot:NoChometzCell="{ props: { dataItem } }">
        <td class="k-table-td">
          <input
            v-if="currentChometzSeasonSelected"
            type="checkbox"
            v-model="dataItem.IsNoChometz"
            class="action-checkbox"
            @change="onNoChometzChanged(dataItem)"
            :disabled="dataItem.ResponseFinalized"
          />
          <font-awesome-icon v-else-if="dataItem.IsNoChometz" class="check-icon" icon="check" />
        </td>
      </template>
      <template v-slot:ReviewedCell="{ props: { dataItem } }">
        <td class="k-table-td">
          <input
            v-if="currentChometzSeasonSelected && (dataItem.SubmittedDate || dataItem.IsNoChometz)"
            type="checkbox"
            v-model="dataItem.ResponseFinalized"
            :disabled="dataItem.ResponseFinalized && !canReverseFinalizationEnabled"
            class="action-checkbox"
            @change="onReviewedChanged(dataItem)"
          />
          <font-awesome-icon
            v-else-if="!currentChometzSeasonSelected && dataItem.ResponseFinalized"
            class="check-icon"
            icon="check"
          />
        </td>
      </template>
      <template v-slot:ContactCell="{ props: { dataItem } }">
        <td class="k-table-td">{{ dataItem.ContactLastName }}, {{ dataItem.ContactFirstName }}</td>
      </template>
      <template v-slot:StatusCell="{ props: { dataItem, field } }">
        <td class="k-table-td">
          <span v-if="dataItem[field]" class="status-cell" :class="{ sent: dataItem[field] }">
            <span>Sent on {{ new Date(dataItem.EmailSentDate).toLocaleDateString() }}</span>
          </span>
        </td>
      </template>
      <template v-slot:ResponseModeCell="{ props: { dataItem } }">
        <td class="k-table-td">
          {{
            dataItem.ResponseMode !== EnumChometzSaleResponseMode.None
              ? EnumChometzSaleResponseMode[dataItem.ResponseMode]
              : ""
          }}
        </td>
      </template>
      <template v-slot:EmailActionCell="{ props }">
        <td class="k-table-td">
          <button
            v-if="currentChometzSeasonSelected"
            class="btn"
            :class="[props.dataItem.EmailSent ? 'btn-secondary' : 'btn-primary']"
            type="button"
            @click="sendEmail(props.dataItem)"
            :disabled="props.dataItem.IsNoChometz || props.dataItem.ResponseFinalized"
          >
            <span v-if="props.dataItem.EmailSent">Resend Email</span>
            <span v-else>Send Email</span>
          </button>
        </td>
      </template>
      <template v-slot:TypeCell="{ props: { dataItem, field } }">
        <td class="k-table-td">
          {{ dataItem[field] ? "Plant" : "Company" }}
        </td>
      </template>
      <template v-slot:TimezoneCell="{ props: { dataItem } }">
        <td class="k-table-td" :class="{ 'timezone-urgent': dataItem.TimezoneEastOfNY }">
          {{ dataItem.TimezoneEastOfNY ? "*** Timezone east of NY" : "" }}
          {{ dataItem.Timezone }}
        </td>
      </template>
      <template v-slot:AttachmentsCell="{ props: { dataItem, field } }">
        <td class="k-table-td">
          <font-awesome-icon v-if="dataItem[field]" class="file-icon" icon="file" @click="showAttachments(dataItem)" />
        </td>
      </template>
      <template v-slot:columnMenu="{ props }">
        <ColumnMenu
          :column="props.column"
          :filterable="props.filterable"
          :filter="props.filter"
          :sortable="props.sortable"
          :sort="props.sort"
          :columns="columns"
          :getItems="getPropValues"
          :hideColumnSelection="true"
          @sortchange="(e) => props.onSortchange(e)"
          @filterchange="(e) => props.onFilterchange(e)"
          @closemenu="(e) => props.onClosemenu(e)"
          @contentfocus="(e) => props.onContentfocus(e)"
          @columnssubmit="onColumnsSubmit"
        />
      </template>
    </Grid>
  </div>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnProps,
  type GridColumnResizeEvent,
  type GridFilterChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
} from "@progress/kendo-vue-grid";
import { ref, watchEffect } from "vue";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import ChometzEmailModal from "./ChometzEmailModal.vue";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import type { ChometzCompany } from "@/Domain/ChometzCompany";
import type { CompositeFilterDescriptor, SortDescriptor } from "@progress/kendo-data-query";
import type { ItemChangedOptions, ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import detailIconUrl from "@/assets/img/DetailIcon.png";
import {
  getFilterOperatorsForBoolean,
  getFilterOperatorsForDate,
  getFilterOperatorsForEnum,
  getFilterOperatorsForNumber,
} from "@/shared/helpers/filtersHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { EnumChometzSaleResponseMode } from "@/Domain/EnumChometzSaleResponseMode";
import ChometzAttachmentsModal from "./ChometzAttachmentsModal.vue";
import GridController from "@/shared/services/GridController";

const props = defineProps<{
  companies: ChometzCompany[];
  currentChometzSeasonSelected: boolean;
  canReverseFinalization?: boolean;
  companiesLoading: boolean;
  pageSize: number;
  skip: number;
  totalCompanies: number;
  filter: CompositeFilterDescriptor | undefined;
  chometzSaleSeason?: number;
  getPropValues: (column: GridColumnPropsExtended) => Promise<ChometzCompany[]>;
}>();

const emit = defineEmits<{
  (e: "companiesChanged", options: ItemChangedOptions): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "detailsClicked", company: ChometzCompany): void;
  (e: "noChometzChanged", company: ChometzCompany): void;
  (e: "reviewedChanged", company: ChometzCompany): void;
}>();

const gridName = "ChometzCompaniesGrid";

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);

const selectedCompany = ref<ChometzCompany>();
const showEmailModal = ref(false);
const showAttachmentsModal = ref(false);
const canReverseFinalizationEnabled = props.canReverseFinalization ?? false;

const sendEmail = function (company: ChometzCompany) {
  selectedCompany.value = company;
  showEmailModal.value = true;
};

const showAttachments = function (company: ChometzCompany) {
  selectedCompany.value = company;
  showAttachmentsModal.value = true;
};

const columns = ref<GridColumnPropsExtended[]>([
  {
    cell: "DetailsCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    width: "40px",
  },
  {
    cell: "NoChometzCell",
    title: "No Chometz",
    sortable: true,
    filterable: true,
    field: "IsNoChometz",
    columnMenu: "columnMenu",
    width: "80px",
    allowedFilterOperators: getFilterOperatorsForBoolean(),
  },
  {
    cell: "ReviewedCell",
    title: "Finalized",
    field: "ResponseFinalized",
    columnMenu: "columnMenu",
    width: "80px",
    allowedFilterOperators: getFilterOperatorsForBoolean(),
  },
  {
    title: "Actions",
    cell: "EmailActionCell",
    width: "100px",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
  },
  {
    title: "Email Sent",
    field: "EmailSent",
    columnMenu: "columnMenu",
    cell: "StatusCell",
    width: "160px",
    allowedFilterOperators: getFilterOperatorsForBoolean(),
  },
  {
    title: "Type",
    field: "TypeString",
    columnMenu: "columnMenu",
    minWidth: 80,
  },
  {
    title: "Company",
    field: "CompanyName",
    columnMenu: "columnMenu",
    minWidth: 100,
  },
  {
    title: "Company ID",
    field: "CompanyId",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForNumber(),
    minWidth: 80,
  },
  {
    title: "Plant",
    field: "PlantName",
    columnMenu: "columnMenu",
    minWidth: 80,
  },
  {
    title: "Plant ID",
    field: "PlantId",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForNumber(),
    minWidth: 80,
  },
  {
    title: "Primary Contact",
    field: "ContactFullName",
    columnMenu: "columnMenu",
    minWidth: 120,
  },
  {
    title: "Contact Email",
    field: "ContactEmail",
    columnMenu: "columnMenu",
    minWidth: 120,
  },
  {
    title: "Contact Phone",
    field: "ContactPhone",
    columnMenu: "columnMenu",
    minWidth: 80,
  },
  {
    title: "Response Customer Name",
    field: "SubmissionCustomerName",
    columnMenu: "columnMenu",
    minWidth: 120,
  },
  {
    title: "Sold through",
    field: "SubmissionTypeString",
    columnMenu: "columnMenu",
    minWidth: 80,
  },
  {
    title: "Response Mode",
    field: "ResponseMode",
    cell: "ResponseModeCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
    minWidth: 80,
  },
  {
    title: "Response",
    field: "SubmissionComment",
    columnMenu: "columnMenu",
    minWidth: 80,
  },
  {
    title: "Response Submitted",
    field: "SubmittedDate",
    columnMenu: "columnMenu",
    type: "date",
    format: "{0:MM/dd/yyyy}",
    allowedFilterOperators: getFilterOperatorsForDate(),
    minWidth: 90,
  },
  {
    title: "Timezone",
    field: "Timezone",
    cell: "TimezoneCell",
    columnMenu: "columnMenu",
    minWidth: 90,
  },
  {
    title: "RC",
    field: "RcFormattedName",
    columnMenu: "columnMenu",
    minWidth: 80,
  },
  {
    title: "Documents",
    field: "HasAttachments",
    cell: "AttachmentsCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    minWidth: 100,
  },
]);

let pageSizes = [10, 20, 25, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);

const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

const gridRef = ref<InstanceType<typeof Grid> | null>(null);

const controller = new GridController<ChometzCompany>(gridName, columns.value);

controller.initOptions();

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("companiesChanged", {
    skip: 0,
    take: take.value,
    filterEvent: e,
  });
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("companiesChanged", {
    skip: event.page.skip,
    take: event.page.take,
    filterEvent: null,
  });
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) {
    columns.value = columnsProps;
    GridOptionsService.saveVisibleColumns(gridName, columnsProps);
  }
  console.debug("ChometzCompaniesGrid.vue, onColumnSubmit", columnsProps);
};

const onColumnResize = function (e: GridColumnResizeEvent) {
  const col = GridOptionsService.getResizedColumn(e, columns.value);
  if (col.title) GridOptionsService.saveColumnWidth(gridName, col.title, e.newWidth);
};

const onNoChometzChanged = function (company: ChometzCompany) {
  emit("noChometzChanged", company);
};

const onReviewedChanged = function (company: ChometzCompany) {
  emit("reviewedChanged", company);
};

const openDetails = function (company: ChometzCompany) {
  emit("detailsClicked", company);
};

defineExpose({
  columns,
});

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});

watchEffect(() => {
  if (gridRef.value && !controller.gridRef.value) {
    controller.setGridRef(gridRef);
  }
});
</script>

<style scoped lang="scss">
.chometz-companies-grid {
  min-height: 450px;

  .check-icon {
    width: 100%;
  }

  .file-icon {
    width: 100%;
    height: 16px;
    color: #337ab7;
    cursor: pointer;

    &:hover {
      color: #0a58ca;
    }
  }

  .btn {
    cursor: pointer;
    border-radius: 6px;
    font-weight: 500;
    display: block;
    width: 100%;
    padding: 10px 8px;
  }
}

.status-cell {
  padding: 3px 12px;
  border-radius: 10px;
  font-size: 13px;
  background: #ffebe9;
  line-height: 1;
  border: 1px solid #f9d5d1;
  font-weight: 500;
  color: #fd6150;

  &.sent {
    color: #008b38;
    background: #cae9d6;
    border-color: #b6d7c3;
  }
}

.timezone-urgent {
  color: #d9534f;
}

.action-checkbox {
  border-radius: 0.25rem;
  border-color: #ced4da;
  background-color: #ffffff;
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}
</style>
